import React, { useEffect, useState } from "react";
import { Container, Row, Col, Table, Button, Modal, Form, Spinner, ToastContainer } from "react-bootstrap";
import { toast } from 'react-toastify'; // Ensure toast is imported from react-toastify
import 'react-toastify/dist/ReactToastify.css';

const SubCategory = () => {
    const [categories, setCategories] = useState([]);  
    const [loading, setLoading] = useState(true);  
    const [error, setError] = useState(null);  
    const [showModal, setShowModal] = useState(false);  
    const [newSubCategoryTitle, setNewSubCategoryTitle] = useState('');  
    const [selectedCategoryId, setSelectedCategoryId] = useState(null);  
    const [selectedSubCategoryId, setSelectedSubCategoryId] = useState(null);  
    const [isSubmitting, setIsSubmitting] = useState(false);  
    const [isEdit, setIsEdit] = useState(false);  

    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const response = await fetch(`https://api.biznetusa.com/api/get-category`);  
                if (!response.ok) {
                    console.error("Failed to fetch categories. Status:", response.status);
                    throw new Error("Failed to fetch categories");
                }

                const result = await response.json();
                if (result.categories) {
                    setCategories(result.categories);
                } else {
                    setError("No categories found in response.");
                }
            } catch (err) {
                console.error("Error fetching categories:", err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchCategories();
    }, []);

    const handleSubmitSubCategory = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setError(null);

        try {
            let url = "https://api.biznetusa.com/api/store-sub-category";
            let method = "POST";
            if (isEdit && selectedSubCategoryId) {
                url = `https://api.biznetusa.com/api/update-sub-category/${selectedSubCategoryId}`;
                method = "PUT";
            }

            const response = await fetch(url, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    cat_id: selectedCategoryId,
                    sub_title: newSubCategoryTitle,
                }),
            });

            const result = await response.json();

            if (response.ok) {
                const updatedCategories = categories.map((category) =>
                    category.id === selectedCategoryId
                        ? {
                            ...category,
                            sub_categories: isEdit
                                ? category.sub_categories.map(subCategory =>
                                    subCategory.id === selectedSubCategoryId
                                        ? { ...subCategory, sub_title: newSubCategoryTitle }
                                        : subCategory
                                )
                                : [...category.sub_categories, result.sub_category],
                        }
                        : category
                );
                setCategories(updatedCategories);  
                setShowModal(false);  
                setNewSubCategoryTitle('');  
                setIsEdit(false);  
                toast.success(isEdit ? 'Subcategory updated successfully!' : 'Subcategory added successfully!');  
            } else {
                setError(result.message || 'Failed to process subcategory.');
                toast.error('Failed to process subcategory.');  
            }
        } catch (err) {
            setError(err.message);
            toast.error('An error occurred while processing subcategory.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleEditSubCategory = (categoryId, subCategory) => {
        if (!subCategory || !subCategory.id) return; 
        setSelectedCategoryId(categoryId);
        setSelectedSubCategoryId(subCategory.id);
        setNewSubCategoryTitle(subCategory.sub_title);
        setIsEdit(true);
        setShowModal(true);  
    };

    const handleDeleteSubCategory = async (subCategoryId) => {
        const confirmDelete = window.confirm("Are you sure you want to delete this subcategory?");
        if (!confirmDelete) return;

        try {
            const response = await fetch(`https://api.biznetusa.com/api/deletesubcategory/${subCategoryId}`, {
                method: "DELETE",
            });

            if (response.ok) {
                const updatedCategories = categories.map((category) => ({
                    ...category,
                    sub_categories: category.sub_categories.filter((subCategory) => subCategory.id !== subCategoryId),
                }));
                setCategories(updatedCategories);  
                toast.success("Subcategory deleted successfully!");  
            } else {
                setError("Failed to delete subcategory.");
                toast.error("Failed to delete subcategory.");  
            }
        } catch (err) {
            setError(err.message);
            toast.error('An error occurred while deleting the subcategory.');
        }
    };

    if (loading) {
        return (
            <Container className="text-center mt-5">
                <Spinner animation="border" />
                <p>Loading categories...</p>
            </Container>
        );
    }

    if (error) {
        return <Container className="text-center text-danger">{error}</Container>;
    }

    return (
        <Container fluid className="mt-4">
            <Row>
                
                <Col lg={12}>
                    <div className="d-flex justify-content-between align-items-center mt-4 mb-3">
                        <h2>Categories and Subcategories</h2>
                        <Button onClick={() => {
                            setIsEdit(false);
                            setShowModal(true);
                        }}>
                            Add Subcategory
                        </Button>
                    </div>

                    <Table responsive bordered hover>
                        <thead>
                            <tr>
                                <th>Category ID</th>
                                <th>Category Title</th>
                                <th>Subcategories</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {categories.length > 0 ? (
                                categories.map((category) => (
                                    <tr key={category.id}>
                                        <td>{category.id}</td>
                                        <td>{category.cat_title}</td>
                                        <td>
                                            <ul className="list-unstyled">
                                                {category.sub_categories.length > 0 ? (
                                                    category.sub_categories.map((subCategory) => (
                                                        <li key={subCategory.id} className="d-flex justify-content-between align-items-center mb-1">
                                                            <span>{subCategory.sub_title}</span>
                                                        </li>
                                                    ))
                                                ) : (
                                                    <p>No subcategories available.</p>
                                                )}
                                            </ul>
                                        </td>
                                        <td>
                                            {category.sub_categories.map((subCategory) => (
                                                <div key={subCategory.id} className="d-flex align-items-center">
                                                    <Button variant="warning" size="sm" onClick={() => handleEditSubCategory(category.id, subCategory)}>
                                                        Edit
                                                    </Button>{' '}
                                                    <Button variant="danger" size="sm" onClick={() => handleDeleteSubCategory(subCategory.id)}>
                                                        Delete
                                                    </Button>
                                                </div>
                                            ))}
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="4">No categories available.</td>
                                </tr>
                            )}
                        </tbody>
                    </Table>
                </Col>
            </Row>

            {/* Modal for adding/editing subcategory */}
            <Modal show={showModal} onHide={() => setShowModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>{isEdit ? "Edit Subcategory" : "Add New Subcategory"}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form onSubmit={handleSubmitSubCategory}>
                        <Form.Group className="mb-3">
                            <Form.Label>Select Category</Form.Label>
                            <Form.Select
                                value={selectedCategoryId || ''}
                                onChange={(e) => setSelectedCategoryId(Number(e.target.value))}
                                required
                                disabled={isEdit}
                            >
                                <option value="">Select a Category</option>
                                {categories.map((category) => (
                                    <option key={category.id} value={category.id}>
                                        {category.cat_title}
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Subcategory Title</Form.Label>
                            <Form.Control
                                type="text"
                                value={newSubCategoryTitle}
                                onChange={(e) => setNewSubCategoryTitle(e.target.value)}
                                required
                            />
                        </Form.Group>
                        <Modal.Footer>
                            <Button type="submit" variant="success" disabled={isSubmitting}>
                                {isSubmitting ? (isEdit ? 'Updating...' : 'Adding...') : (isEdit ? 'Update Subcategory' : 'Add Subcategory')}
                            </Button>
                            <Button variant="secondary" onClick={() => setShowModal(false)}>
                                Cancel
                            </Button>
                        </Modal.Footer>
                    </Form>
                </Modal.Body>
            </Modal>

            <ToastContainer />
        </Container>
    );
};

export default SubCategory;
